import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import '../../styles/SnippetModal.scss';

const SnippetModal = ({
  show,
  onHide,
  mode, // 'create', 'edit', 'delete'
  snippet = null,
  onSave,
  onDelete,
  wabaId,
  accessToken
}) => {
  const [formData, setFormData] = useState({
    name: '',
    body: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && snippet) {
      setFormData({
        name: snippet.name || '',
        body: snippet.body || snippet.content || ''
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        body: ''
      });
    }
    setErrors({});
    setApiError('');
  }, [mode, snippet, show]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Snippet name is required';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Snippet content is required';
    }

    // Validate snippet name length
    if (formData.name.trim().length > 100) {
      newErrors.name = 'Snippet name must be 100 characters or less';
    }

    // Validate content length
    if (formData.body.trim().length > 1000) {
      newErrors.body = 'Snippet content must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear API error when user makes changes
    if (apiError) {
      setApiError('');
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check for required props
    if (!wabaId || !accessToken) {
      setApiError('Missing required authentication or account information');
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      await onSave({
        ...formData,
        id: snippet?.id,
        lastModified: new Date().toISOString()
      });
      onHide();
    } catch (error) {
      console.error('Error saving snippet:', error);

      // Handle specific API errors
      if (error.message.includes('already exists')) {
        setErrors({ name: 'A snippet with this name already exists' });
      } else if (error.message.includes('Permission denied')) {
        setApiError('You do not have permission to perform this action');
      } else if (error.message.includes('Invalid variables')) {
        setApiError('Invalid variable format in snippet content');
      } else {
        setApiError(error.message || 'Failed to save snippet. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!accessToken) {
      setApiError('Missing authentication information');
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      await onDelete(snippet.id);
      onHide();
    } catch (error) {
      console.error('Error deleting snippet:', error);

      if (error.message.includes('Permission denied')) {
        setApiError('You do not have permission to delete this snippet');
      } else if (error.message.includes('not found')) {
        setApiError('Snippet not found or already deleted');
      } else {
        setApiError(error.message || 'Failed to delete snippet. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create': return 'Add New Snippet';
      case 'edit': return 'Edit Snippet';
      case 'delete': return `Remove Snippet - ${snippet?.name || ''}`;
      default: return 'Snippet';
    }
  };

  const renderDeleteContent = () => (
    <div className="snippet-modal-delete-content">
      {/* API Error Display */}
      {apiError && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {apiError}
        </div>
      )}

      <div className="delete-icon-wrapper">
        <div className="delete-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
      </div>
      <div className="delete-message">
        <p>Are you sure you want to remove <strong>{snippet?.name}</strong> from your Snippets?</p>
        <p className="text-muted">This action cannot be undone.</p>
      </div>
    </div>
  );

  const renderFormContent = () => (
    <div className="snippet-modal-form">
      {/* API Error Display */}
      {apiError && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {apiError}
        </div>
      )}

      <Form.Group className="snippet-form-group">
        <Form.Label>Snippet Name</Form.Label>
        <Form.Control
          type="text"
          placeholder={mode === 'edit' ? formData.name : 'Enter snippet name'}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          isInvalid={!!errors.name}
          className={errors.name ? 'error' : ''}
          maxLength={100}
        />
        {errors.name && (
          <Form.Control.Feedback type="invalid">
            {errors.name}
          </Form.Control.Feedback>
        )}
        <Form.Text className="text-muted">
          {formData.name.length}/100 characters
        </Form.Text>
      </Form.Group>

      <Form.Group className="snippet-form-group">
        <Form.Label>Snippet Content</Form.Label>
        <Form.Control
          as="textarea"
          rows={6}
          placeholder={mode === 'edit' ? formData.body : 'Enter snippet content. You can use variables like {{$customerName}} for dynamic content.'}
          value={formData.body}
          onChange={(e) => handleInputChange('body', e.target.value)}
          isInvalid={!!errors.body}
          className={errors.body ? 'error' : ''}
          maxLength={1000}
        />
        {errors.body && (
          <Form.Control.Feedback type="invalid">
            {errors.body}
          </Form.Control.Feedback>
        )}
        <Form.Text className="text-muted">
          {formData.body.length}/1000 characters. Use {'{{$variableName}}'} for dynamic content.
        </Form.Text>
      </Form.Group>
    </div>
  );

  const renderFooterButtons = () => {
    if (mode === 'delete') {
      return (
        <>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={isLoading}
            className="btn-ghost"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isLoading}
            className="btn-delete"
          >
            {isLoading ? 'Removing...' : 'Remove'}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={isLoading}
          className="btn-ghost"
        >
          Discard
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create Snippet')}
        </Button>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="snippet-modal"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>{getModalTitle()}</Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          disabled={isLoading}
        >
          <i className="fas fa-times"></i>
        </button>
      </Modal.Header>

      <Modal.Body>
        {mode === 'delete' ? renderDeleteContent() : renderFormContent()}
      </Modal.Body>

      <Modal.Footer>
        {renderFooterButtons()}
      </Modal.Footer>
    </Modal>
  );
};

export default SnippetModal;
